import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, BarChart3 } from "lucide-react"; // ✅ icons
import axios from "axios";
import Navbar from "./Navbar";

const AdherenceReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adherence, setAdherence] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAdherenceReport = async () => {
    const patient_id = location.state?.patient_id;
    if (!patient_id) return;

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/doctor_create_report_excel",
        JSON.stringify({ patient_id }),
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setAdherence(response.data || {});
      }
    } catch (error) {
      console.error("Error fetching adherence report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdherenceReport();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      <Navbar />

      {/* Main Container */}
      <div className="flex justify-center items-start px-4 sm:px-6 md:px-8 pt-28 pb-12">
        <div className="w-full max-w-3xl bg-white rounded-3xl shadow-xl border border-green-100 p-6 sm:p-10 transition-all">
          
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-green-100 p-3 rounded-full shadow-md">
              <BarChart3 className="w-7 h-7 sm:w-9 sm:h-9 text-green-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-green-800">
              Adherence Report
            </h1>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="text-center text-green-600 font-medium text-lg">
              Loading adherence report...
            </div>
          ) : !adherence ? (
            <div className="text-center text-gray-600 font-medium text-lg">
              No adherence report available for this patient.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overall % */}
              <div className="bg-gradient-to-r from-green-100 to-emerald-50 rounded-2xl p-6 text-center shadow-md">
                <p className="text-xl font-semibold text-green-800">
                  Overall Adherence
                </p>
                <p className="text-4xl font-bold text-green-600 mt-2">
                  {adherence.adherence_percentage || 0}%
                </p>
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-2xl p-4 text-center shadow-sm">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-lg font-semibold text-green-700">Taken</p>
                  <p className="text-2xl font-bold text-green-600">
                    {adherence.pills_taken || 0}
                  </p>
                </div>
                <div className="bg-red-50 rounded-2xl p-4 text-center shadow-sm">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-lg font-semibold text-red-700">Missed</p>
                  <p className="text-2xl font-bold text-red-600">
                    {adherence.pills_missed || 0}
                  </p>
                </div>
              </div>

              {/* Timeline List (if available) */}
              {adherence.timeline && adherence.timeline.length > 0 && (
                <div className="mt-6">
                  <h2 className="text-lg font-semibold text-green-800 mb-3">
                    Timeline
                  </h2>
                  <ul className="space-y-3">
                    {adherence.timeline.map((entry, index) => (
                      <li
                        key={index}
                        className="flex justify-between items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-2"
                      >
                        <span className="text-gray-700">
                          {entry.date} - {entry.time}
                        </span>
                        <span
                          className={`font-medium ${
                            entry.status === "Taken"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {entry.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Back Button */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-full shadow-md hover:shadow-lg transition-transform transform hover:scale-105"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdherenceReport;
